import axiosInstance from "@/lib/axiosInstance";
import { stopLocationWatch } from "@/lib/geoLocation";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { useAssetsFilterBaseStore } from "@/store/assetsFilterStore";
import { useAssignServiceProviderStore } from "@/store/assignServiceProviderStore";
import { useClientsFilterBaseStore } from "@/store/clientsFilterStore";
import { useEntitiesFilterBaseStore } from "@/store/entitiesFilterStore";
import { useEntitiesStore } from "@/store/entitiesStore";
import { useEmailAiInboxStore } from "@/store/emailAiInboxStore";
import { useLocationStore } from "@/store/locationStore";
import { useRolesStore } from "@/store/rolesStore";
import { SESSION_STORE_KEY, useSessionStore } from "@/store/sessionStore/sessionStore";
import type { TSessionData } from "@/store/sessionStore/sessionStore.types";
import { useSPFilterBaseStore } from "@/store/serviceProviderFilterStore";
import { useTableFilterBaseStore } from "@/store/tableFilterStore";
import { useFilterBarStore } from "@/store/filterBarStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { useServiceProviderOnboardingStore } from "@/store/useServiceProviderOnboardingStore";
import { useSpInviteSessionStore } from "@/store/useSpInviteSessionStore";
import { useUserStore } from "@/store/userStore/userStore";
import { useWorkVisitStore } from "@/store/workVisitStore";
import { useBreadcrumbStore } from "@/store/useBreadcrumbStore";
import { useClientOnboardingStore } from "@/store/clientOnboardingStore";
import { useCoverageStore } from "@/store/coverageStore/coverageStore";
import { deleteAllFilterPreferences } from "@/api/view-preferences/viewPreferences.queries";
import { resetEmailAiAuth } from "@/api/email-ai/emailAi.auth";
import { useMutation } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import type { CognitoUserSession } from "amazon-cognito-identity-js";
import { tryTransferSession } from "@/lib/sessionSync";
import { saveCurrentLocationForReturn, saveReturnPath } from "@/utils/postLoginReturn";

export function isAuthenticated(): boolean {
  const { userId } = useSessionStore.getState();
  return !!userId;
}

export const requireAuth = async (location?: { href?: string }) => {
  if (!isAuthenticated()) {
    await tryTransferSession();
  }
  if (!isAuthenticated()) {
    if (location?.href) saveReturnPath(location.href);
    else saveCurrentLocationForReturn();
    throw redirect({ to: "/login" });
  }
};

const safeParseModules = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

function parseBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
}

export const applySessionToStores = (session: CognitoUserSession) => {
  const accessToken = session.getAccessToken().getJwtToken();
  const userId = session.getAccessToken().payload.sub;
  const jwtUserId = session.getIdToken().payload.userId;
  const userEmail = session.getIdToken().payload.email;
  const userName = session.getIdToken().payload.userName;
  const userRoles = session.getIdToken().payload.userRoles;
  const userType = session.getIdToken().payload?.userType;
  const modulesRaw = session.getIdToken().payload?.modules;
  const modules = safeParseModules(modulesRaw);
  const entityName = session.getIdToken().payload?.entityName;
  const isEntityLoginRaw = session.getIdToken().payload?.isEntityLogin;
  const isMainAppLoginRaw = session.getIdToken().payload?.isMainAppLogin;
  const isEntityLogin = parseBoolean(isEntityLoginRaw);
  const isMainAppLogin = parseBoolean(isMainAppLoginRaw);
  const tenantId = session.getIdToken().payload?.tenantId;

  const {
    setUserJwt,
    setUserId,
    setUserType,
    setModules,
    setJwtUserId,
    setUserEmail,
    setUserName,
    setUserRoles,
    setEntityName,
    setIsEntityLogin,
    setTenantId,
    setIsMainAppLogin,
  } = useUserStore.getState();

  setUserJwt(accessToken);
  setUserId(userId);
  setUserType(userType);
  setModules(modules);
  setJwtUserId(jwtUserId);
  setUserEmail(userEmail);
  setUserName(userName);
  setUserRoles(safeParseModules(userRoles));
  setEntityName(entityName);
  setIsEntityLogin(isEntityLogin);
  setTenantId(tenantId);
  setIsMainAppLogin(isMainAppLogin);
};

export const applySessionResponseToStore = (response: TSessionData) => {
  const { setSessionData } = useSessionStore.getState();
  setSessionData({
    ...response,
    isReactiveHire: response.isReactiveHire ?? false,
    allEntities: response.allEntities ?? false,
  });
};

export const clearAllStores = () => {
  const { resetUserStore } = useUserStore.getState();
  const { resetRoleStore } = useRolesStore.getState();
  const { resetSessionStore } = useSessionStore.getState();
  const { resetImpersonationStore } = useImpersonationStore.getState();
  const { clearServiceProviderId } = useServiceProviderOnboardingStore.getState();
  const { resetSpInviteSession } = useSpInviteSessionStore.getState();
  const { resetAllFilters: resetTableFilters } = useTableFilterBaseStore.getState();
  const { resetAllFilters: resetSPFilters } = useSPFilterBaseStore.getState();
  const { resetAllFilters: resetClientsFilters } = useClientsFilterBaseStore.getState();
  const { resetAllFilters: resetAssetsFilters } = useAssetsFilterBaseStore.getState();
  const { resetAllFilters: resetEntitiesFilters } = useEntitiesFilterBaseStore.getState();
  const { resetAll: resetFilterBarVisibility } = useFilterBarStore.getState();
  const { clearWorkVisitDetails } = useWorkVisitStore.getState();
  const { resetAssignServiceProviderStore } = useAssignServiceProviderStore.getState();
  const { setLastCreatedEntity } = useEntitiesStore.getState();
  const { setTimezone } = useLocationStore.getState();
  const { clearBreadcrumbs } = useBreadcrumbStore.getState();
  const { resetOnboarding } = useClientOnboardingStore.getState();
  const { resetCoverageStore } = useCoverageStore.getState();
  const { resetSelectedInboxes } = useEmailAiInboxStore.getState();

  resetUserStore();
  resetRoleStore();
  resetSessionStore();
  resetImpersonationStore();
  clearServiceProviderId();
  resetSpInviteSession();
  resetTableFilters();
  resetSPFilters();
  resetClientsFilters();
  resetAssetsFilters();
  resetEntitiesFilters();
  resetFilterBarVisibility();
  clearWorkVisitDetails();
  resetAssignServiceProviderStore();
  setLastCreatedEntity(null);
  setTimezone("");
  // Reset persisted stores before removing their localStorage keys so the
  // persist subscriber's synchronous write is immediately overridden by removeItem.
  resetOnboarding();
  clearBreadcrumbs();
  resetCoverageStore();
  // The Email AI id token is module state, not a store, and logout never reloads
  // the page. Left cached it would authenticate the next user in this tab as the
  // previous one, and that token is what scopes the mailbox.
  resetEmailAiAuth();
  resetSelectedInboxes();
  stopLocationWatch();

  localStorage.removeItem("user-store");
  localStorage.removeItem("roles-store");
  localStorage.removeItem(SESSION_STORE_KEY);
  sessionStorage.removeItem(SESSION_STORE_KEY);
  localStorage.removeItem("entity-tenant");
  localStorage.removeItem("client-onboarding-storage");
  localStorage.removeItem("service-provider-onboarding-store");
  localStorage.removeItem("sp-invite-session-store");
  localStorage.removeItem("impersonation-store");
  localStorage.removeItem("work-visit-store");
  localStorage.removeItem("assign-service-provider-store");
  localStorage.removeItem("entities-store");
  localStorage.removeItem("location-store");
  localStorage.removeItem("breadcrumb-store");

  queryClient.clear();
};

export const performLogout = async () => {
  try {
    await deleteAllFilterPreferences();
  } catch (err) {
    console.error("Error clearing view preferences:", err);
  }
  try {
    await axiosInstance.post("/session-management/logout", { withCredentials: true });
  } catch (err) {
    console.error("Error during sign-out:", err);
  } finally {
    clearAllStores();
  }
};

export const useLogout = () => {
  return useMutation({
    mutationFn: performLogout,
  });
};
